import React, { useState } from "react";
import {
  ListChecks,
  Plus,
  X,
  Edit2,
  Check,
  Calendar,
  CheckCircle2,
  Circle,
  Link2,
  ChevronUp,
  ChevronDown,
  ChevronRight,
  Repeat,
  History,
  CheckCheck,
  GraduationCap,
  Bell,
} from "lucide-react";
import { formatDisplayDate, isPast, formatDate } from "../utils/dateUtils";
import { renderRichText } from "../utils/richText";
import {
  WEEKDAYS,
  isRecurring,
  getNextOccurrence,
  weekdayShortList,
} from "../utils/recurrence";
import RecurrenceHistoryModal from "./RecurrenceHistoryModal";

const Remember = ({
  items = [],
  quickLinkItems = [],
  conferenceItems = [],
  showInReminders = true,
  onToggleShowInReminders,
  onAddItem,
  onToggleItem,
  onEditItem,
  onDeleteItem,
  onUpdateDueDate,
  onToggleQuickLink,
  onToggleRecurringOccurrence,
  onCompleteRecurringSeries,
  onUpdateRecurrence,
  onCompleteConferenceRemember,
  onMovePanelUp,
  onMovePanelDown,
  isFirstPanel,
  isLastPanel,
}) => {
  const [newText, setNewText] = useState("");
  const [newDueDate, setNewDueDate] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  // "Done" section is collapsed by default; click the header to expand it.
  const [showDone, setShowDone] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingText, setEditingText] = useState("");
  // Recurrence: add-form state
  const [newIsRecurring, setNewIsRecurring] = useState(false);
  const [newWeekdays, setNewWeekdays] = useState([]);
  const [newEndDate, setNewEndDate] = useState("");
  // Recurrence: edit state (only used while editing a recurring item)
  const [editWeekdays, setEditWeekdays] = useState([]);
  const [editEndDate, setEditEndDate] = useState("");
  // History modal
  const [historyItem, setHistoryItem] = useState(null);

  const todayStr = formatDate(new Date());

  const toggleWeekday = (list, setList, value) => {
    setList(
      list.includes(value) ? list.filter((d) => d !== value) : [...list, value],
    );
  };

  // Weekday selector used by both the add form and the edit form.
  const WeekdayPicker = ({ selected, onToggle }) => (
    <div className="flex flex-wrap gap-1">
      {WEEKDAYS.map((w) => {
        const on = selected.includes(w.value);
        return (
          <button
            key={w.value}
            type="button"
            onClick={() => onToggle(w.value)}
            title={w.label}
            className={`w-9 h-8 rounded-lg text-xs font-semibold transition-colors ${
              on
                ? "bg-purple-500 text-white"
                : "bg-gray-700 text-gray-300 hover:bg-gray-600"
            }`}
          >
            {w.short}
          </button>
        );
      })}
    </div>
  );

  const recurringValid = newWeekdays.length > 0 && !!newEndDate;
  const canAdd = !!newText.trim() && (!newIsRecurring || recurringValid);

  const resetAddForm = () => {
    setNewText("");
    setNewDueDate("");
    setNewIsRecurring(false);
    setNewWeekdays([]);
    setNewEndDate("");
    setShowAdd(false);
  };

  const handleAdd = () => {
    if (!newText.trim()) return;
    if (newIsRecurring) {
      if (!recurringValid) return;
      onAddItem(newText.trim(), null, {
        weekdays: newWeekdays,
        endDate: newEndDate,
      });
    } else {
      onAddItem(newText.trim(), newDueDate || null);
    }
    resetAddForm();
  };

  const startEditing = (item) => {
    setEditingId(item.id);
    setEditingText(item.text);
    if (isRecurring(item)) {
      setEditWeekdays(item.recurrence.weekdays || []);
      setEditEndDate(item.recurrence.endDate || "");
    } else {
      setEditWeekdays([]);
      setEditEndDate("");
    }
  };

  const saveEdit = () => {
    const editingItem = items.find((i) => i.id === editingId);
    if (editingText.trim()) {
      onEditItem(editingId, editingText.trim());
    }
    // Persist recurrence changes (today-and-future only; past records kept).
    if (
      editingItem &&
      isRecurring(editingItem) &&
      editWeekdays.length > 0 &&
      editEndDate &&
      onUpdateRecurrence
    ) {
      onUpdateRecurrence(editingId, {
        weekdays: editWeekdays,
        endDate: editEndDate,
      });
    }
    setEditingId(null);
    setEditingText("");
    setEditWeekdays([]);
    setEditEndDate("");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingText("");
    setEditWeekdays([]);
    setEditEndDate("");
  };

  // Effective date used for sorting: recurring items use their next occurrence.
  const effectiveDate = (item) =>
    isRecurring(item) ? getNextOccurrence(item) : item.dueDate;

  // Active items first (sorted by date, undated last), completed below.
  const sortByDueDate = (a, b) => {
    const da = effectiveDate(a);
    const db = effectiveDate(b);
    if (!da && !db) return 0;
    if (!da) return 1;
    if (!db) return -1;
    return new Date(da) - new Date(db);
  };

  // A recurring item is "done" when its series is ended OR there are no more
  // upcoming occurrences (end date passed). Normal/quick-link items use their
  // own completed flag.
  const isDone = (item) =>
    item.completed || (isRecurring(item) && !getNextOccurrence(item));

  // Quick-link cards (remember tag on) are shown alongside manual items but are
  // read-only here: they can be ticked complete, but their text/date/deletion is
  // managed from the Quick Links panel itself.
  const normalizedQuickLinks = (quickLinkItems || []).map((q) => ({
    ...q,
    source: "quicklink",
  }));
  // Conference items: decision-date rows are display-only; remember-flagged rows
  // can be completed here (clears the flag on the conference card).
  const normalizedConferences = (conferenceItems || []).map((c) => ({
    ...c,
    source: "conference",
    conferenceKind: c.kind || "decision",
  }));
  const allItems = [
    ...items.map((i) => ({ ...i, source: "remember" })),
    ...normalizedQuickLinks,
    ...normalizedConferences,
  ];

  const activeItems = allItems.filter((i) => !isDone(i)).sort(sortByDueDate);
  const completedItems = allItems.filter((i) => isDone(i)).sort(sortByDueDate);

  const handleToggle = (item) => {
    if (item.source === "conference") {
      if (item.conferenceKind === "remember" && onCompleteConferenceRemember) {
        onCompleteConferenceRemember(item.cardId, item.entryId);
      }
      return;
    }
    if (item.source === "quicklink") {
      onToggleQuickLink && onToggleQuickLink(item.id);
    } else {
      onToggleItem(item.id);
    }
  };

  // Recurring Remember item: purple accent, weekday + end-date indicator,
  // per-occurrence completion, end-series, history and recurrence editing.
  const renderRecurringItem = (item) => {
    const next = getNextOccurrence(item);
    const done = isDone(item);
    const shorts = weekdayShortList(item.recurrence.weekdays || []);
    const editing = editingId === item.id;

    if (editing) {
      return (
        <div
          key={`remember-${item.id}`}
          className="bg-gray-700 p-3 rounded-2xl border-2 border-purple-500/60 space-y-2"
        >
          <input
            type="text"
            value={editingText}
            onChange={(e) => setEditingText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") cancelEdit();
            }}
            autoFocus
            className="w-full bg-gray-600 text-cyan-200 px-3 py-1.5 rounded-lg border-2 border-purple-500 outline-none"
          />
          <div>
            <div className="text-xs text-purple-300 mb-1">Repeats on</div>
            <WeekdayPicker
              selected={editWeekdays}
              onToggle={(v) => toggleWeekday(editWeekdays, setEditWeekdays, v)}
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-purple-300">Ends</span>
            <input
              type="date"
              value={editEndDate}
              min={todayStr}
              onChange={(e) => setEditEndDate(e.target.value)}
              className="bg-gray-600 text-cyan-200 px-2 py-1 rounded-lg border border-gray-500 focus:border-purple-500 outline-none text-sm"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={saveEdit}
              disabled={editWeekdays.length === 0 || !editEndDate}
              className="flex-1 bg-purple-500 hover:bg-purple-600 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-semibold py-1.5 rounded-lg transition-colors text-sm"
            >
              Save
            </button>
            <button
              onClick={cancelEdit}
              className="px-4 py-1.5 bg-gray-600 hover:bg-gray-500 text-white rounded-lg transition-colors text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      );
    }

    return (
      <div
        key={`remember-${item.id}`}
        className="flex items-center gap-2 bg-purple-900/20 p-3 rounded-2xl group border border-purple-500/40 hover:bg-purple-900/30 transition-all"
      >
        {/* Tick the current/next occurrence (or show done) */}
        <button
          onClick={() => next && onToggleRecurringOccurrence(item.id, next)}
          disabled={!next}
          className="flex-shrink-0 disabled:cursor-default"
          title={
            done
              ? "Recurrence finished"
              : `Mark ${next ? formatDisplayDate(new Date(next)) : "occurrence"} done`
          }
        >
          {done ? (
            <CheckCircle2 className="w-6 h-6 text-green-400" />
          ) : (
            <Circle className="w-6 h-6 text-purple-400" />
          )}
        </button>

        <span
          className={`flex-1 flex items-center gap-2 min-w-0 ${
            done ? "line-through text-gray-400" : "text-purple-100"
          }`}
        >
          <Repeat className="w-4 h-4 text-purple-400 flex-shrink-0" />
          <span className="truncate">
            {renderRichText(item.text, `rem-${item.id}`)}
          </span>
        </span>

        {/* Weekday + end-date indicator (replaces the due date slot) */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="hidden sm:flex gap-0.5">
            {shorts.map((d) => (
              <span
                key={d}
                className="bg-purple-500/20 text-purple-200 text-[10px] px-1 py-0.5 rounded"
              >
                {d}
              </span>
            ))}
          </span>
          <span className="text-xs text-purple-300 flex items-center gap-1">
            <Calendar className="w-3.5 h-3.5" />
            {item.recurrence.endDate
              ? `til ${formatDisplayDate(new Date(item.recurrence.endDate))}`
              : ""}
          </span>
        </div>

        {/* Actions */}
        <div className="flex gap-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
          <button
            onClick={() => setHistoryItem(item)}
            className="flex-shrink-0"
            title="View occurrence history"
          >
            <History className="w-5 h-5 text-purple-300 hover:text-purple-200" />
          </button>
          {!done && (
            <button
              onClick={() => {
                if (
                  window.confirm(
                    "Mark ALL recurrences complete and end this series?",
                  )
                ) {
                  onCompleteRecurringSeries(item.id);
                }
              }}
              className="flex-shrink-0"
              title="Complete all / end series"
            >
              <CheckCheck className="w-5 h-5 text-green-400 hover:text-green-300" />
            </button>
          )}
          <button
            onClick={() => startEditing(item)}
            className="flex-shrink-0"
            title="Edit recurrence"
          >
            <Edit2 className="w-5 h-5 text-yellow-400 hover:text-yellow-300" />
          </button>
          <button
            onClick={() => onDeleteItem(item.id)}
            className="flex-shrink-0"
            title="Delete"
          >
            <X className="w-5 h-5 text-red-400 hover:text-red-300" />
          </button>
        </div>
      </div>
    );
  };

  const renderItem = (item) => {
    if (item.source === "remember" && isRecurring(item)) {
      return renderRecurringItem(item);
    }
    const overdue =
      item.dueDate && !item.completed && isPast(new Date(item.dueDate));
    const isQuickLink = item.source === "quicklink";
    const isConference = item.source === "conference";
    const isConferenceRemember =
      isConference && item.conferenceKind === "remember";
    return (
      <div
        key={`${item.source}-${item.id}`}
        className="flex items-center gap-2 bg-gray-700 p-3 rounded-2xl group hover:bg-gray-600 transition-all"
      >
        {/* Tick / untick (decision-date conference rows are display-only) */}
        {isConference && !isConferenceRemember ? (
          <span
            className="flex-shrink-0"
            title="Awaiting decision (manage on the Conferences card)"
          >
            <GraduationCap className="w-6 h-6 text-amber-400" />
          </span>
        ) : (
          <button
            onClick={() => handleToggle(item)}
            className="flex-shrink-0"
            title={
              item.completed
                ? "Bring back to remember"
                : "Done / no longer needed"
            }
          >
            {item.completed ? (
              <CheckCircle2 className="w-6 h-6 text-green-400" />
            ) : (
              <Circle className="w-6 h-6 text-cyan-400" />
            )}
          </button>
        )}

        {/* Text or edit input */}
        {editingId === item.id && !isQuickLink && !isConference ? (
          <input
            type="text"
            value={editingText}
            onChange={(e) => setEditingText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") saveEdit();
              else if (e.key === "Escape") cancelEdit();
            }}
            onBlur={saveEdit}
            autoFocus
            className="flex-1 bg-gray-600 text-cyan-200 px-3 py-1 rounded-lg border-2 border-cyan-500 outline-none"
          />
        ) : (
          <span
            className={`flex-1 flex items-center gap-2 ${
              item.completed ? "line-through text-gray-400" : "text-cyan-200"
            }`}
          >
            <span>{renderRichText(item.text, `rem-${item.id}`)}</span>
            {isQuickLink && (
              <span className="inline-flex items-center gap-1 text-xs text-cyan-400/80 bg-cyan-500/10 px-1.5 py-0.5 rounded-full flex-shrink-0 no-underline">
                <Link2 className="w-3 h-3" />
                Quick Link
              </span>
            )}
            {isConference && (
              <span className="inline-flex items-center gap-1 text-xs text-amber-400/80 bg-amber-500/10 px-1.5 py-0.5 rounded-full flex-shrink-0 no-underline">
                <GraduationCap className="w-3 h-3" />
                Conference
              </span>
            )}
          </span>
        )}

        {/* Due date display + picker */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {isQuickLink || isConference ? (
            item.dueDate && (
              <span
                className={`text-sm flex items-center gap-1 ${
                  overdue ? "text-red-300" : "text-gray-400"
                }`}
              >
                <Calendar className="w-4 h-4" />
                {formatDisplayDate(new Date(item.dueDate))}
              </span>
            )
          ) : item.dueDate ? (
            <>
              <span
                className={`text-sm flex items-center gap-1 ${
                  overdue ? "text-red-300" : "text-gray-400"
                }`}
              >
                <Calendar className="w-4 h-4" />
                {formatDisplayDate(new Date(item.dueDate))}
              </span>
              <input
                type="date"
                value={item.dueDate}
                onChange={(e) =>
                  onUpdateDueDate(item.id, e.target.value || null)
                }
                className="bg-gray-600 text-cyan-200 px-2 py-1 rounded-lg border border-gray-500 focus:border-cyan-500 outline-none text-sm opacity-0 group-hover:opacity-100 transition-opacity w-32"
                title="Change due date"
              />
            </>
          ) : (
            <input
              type="date"
              value=""
              onChange={(e) => onUpdateDueDate(item.id, e.target.value || null)}
              className="bg-gray-600 text-cyan-200 px-2 py-1 rounded-lg border border-gray-500 focus:border-cyan-500 outline-none text-sm opacity-0 group-hover:opacity-100 transition-opacity"
              title="Set due date"
            />
          )}
        </div>

        {/* Action buttons (manual remember items only) */}
        {!isQuickLink && !isConference && (
          <div className="flex gap-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
            {editingId === item.id ? (
              <button onClick={saveEdit} className="flex-shrink-0" title="Save">
                <Check className="w-5 h-5 text-green-400 hover:text-green-300" />
              </button>
            ) : (
              <button
                onClick={() => startEditing(item)}
                className="flex-shrink-0"
                title="Edit"
              >
                <Edit2 className="w-5 h-5 text-yellow-400 hover:text-yellow-300" />
              </button>
            )}
            <button
              onClick={() => onDeleteItem(item.id)}
              className="flex-shrink-0"
              title="Delete"
            >
              <X className="w-5 h-5 text-red-400 hover:text-red-300" />
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="bg-gray-800 rounded-3xl p-4 sm:p-6 border-2 border-cyan-500/30 shadow-lg">
      <div className="flex items-center justify-between mb-4 gap-2">
        <div className="flex items-center gap-2 flex-wrap min-w-0">
          <ListChecks className="w-5 h-5 text-cyan-400 flex-shrink-0" />
          <h3 className="text-xl font-bold text-cyan-400">Remember</h3>
          <span className="text-xs text-gray-500">({activeItems.length})</span>
          {onToggleShowInReminders && (
            <button
              type="button"
              onClick={() => onToggleShowInReminders(!showInReminders)}
              aria-pressed={showInReminders}
              title={
                showInReminders
                  ? "Remember items with a due date appear in Reminders"
                  : "Remember items with a due date stay in this panel only"
              }
              className={`flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full border transition-colors ${
                showInReminders
                  ? "bg-cyan-500/15 border-cyan-500/50 text-cyan-300"
                  : "bg-gray-700 border-gray-600 text-gray-400 hover:border-cyan-500/40"
              }`}
            >
              <Bell className="w-3.5 h-3.5 flex-shrink-0" />
              Show in Reminders
              <span
                className={`relative inline-flex h-4 w-7 flex-shrink-0 items-center rounded-full transition-colors ${
                  showInReminders ? "bg-cyan-500" : "bg-gray-600"
                }`}
              >
                <span
                  className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                    showInReminders ? "translate-x-3.5" : "translate-x-0.5"
                  }`}
                />
              </span>
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          {(onMovePanelUp || onMovePanelDown) && (
            <div className="flex items-center gap-1">
              <button
                onClick={onMovePanelUp}
                disabled={isFirstPanel}
                className="p-1.5 rounded-lg bg-gray-700 hover:bg-gray-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                title="Move panel up"
              >
                <ChevronUp className="w-4 h-4 text-cyan-300" />
              </button>
              <button
                onClick={onMovePanelDown}
                disabled={isLastPanel}
                className="p-1.5 rounded-lg bg-gray-700 hover:bg-gray-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                title="Move panel down"
              >
                <ChevronDown className="w-4 h-4 text-cyan-300" />
              </button>
            </div>
          )}
          <button
            onClick={() => setShowAdd(!showAdd)}
            className="p-2 bg-cyan-500 hover:bg-cyan-600 rounded-xl transition-colors"
            title="Add something to remember"
          >
            {showAdd ? (
              <X className="w-5 h-5 text-white" />
            ) : (
              <Plus className="w-5 h-5 text-white" />
            )}
          </button>
        </div>
      </div>

      {/* Add form */}
      {showAdd && (
        <div className="mb-4 p-4 bg-gray-700/50 rounded-xl border-2 border-cyan-500/50 space-y-2">
          <input
            type="text"
            value={newText}
            onChange={(e) => setNewText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleAdd();
            }}
            placeholder="Something to remember..."
            className="w-full bg-gray-700 text-cyan-200 px-3 py-2 rounded-lg border-2 border-gray-600 focus:border-cyan-500 outline-none"
            autoFocus
          />
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-cyan-400 flex-shrink-0" />
            <label className="text-sm text-cyan-300 flex-shrink-0">
              Due Date (Optional):
            </label>
            <input
              type="date"
              value={newDueDate}
              disabled={newIsRecurring}
              onChange={(e) => setNewDueDate(e.target.value)}
              className="bg-gray-700 text-cyan-200 px-3 py-1.5 rounded-xl border-2 border-gray-600 focus:border-cyan-500 outline-none text-sm disabled:opacity-40 disabled:cursor-not-allowed"
            />
            {newDueDate && !newIsRecurring && (
              <button
                onClick={() => setNewDueDate("")}
                className="text-xs text-gray-400 hover:text-cyan-300 transition-colors"
              >
                Clear
              </button>
            )}
          </div>

          {/* Recurring toggle */}
          <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
            <input
              type="checkbox"
              checked={newIsRecurring}
              onChange={(e) => {
                setNewIsRecurring(e.target.checked);
                if (e.target.checked) setNewDueDate("");
              }}
              className="accent-purple-500 w-4 h-4"
            />
            <Repeat className="w-4 h-4 text-purple-400" />
            <span className="text-purple-300">Recurring (weekly)</span>
          </label>

          {newIsRecurring && (
            <div className="p-3 rounded-xl bg-purple-900/20 border border-purple-500/40 space-y-2">
              <div>
                <div className="text-xs text-purple-300 mb-1">Repeats on</div>
                <WeekdayPicker
                  selected={newWeekdays}
                  onToggle={(v) =>
                    toggleWeekday(newWeekdays, setNewWeekdays, v)
                  }
                />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-purple-300 flex-shrink-0">
                  End date (required):
                </span>
                <input
                  type="date"
                  value={newEndDate}
                  min={todayStr}
                  onChange={(e) => setNewEndDate(e.target.value)}
                  className="bg-gray-700 text-cyan-200 px-3 py-1.5 rounded-xl border-2 border-gray-600 focus:border-purple-500 outline-none text-sm"
                />
              </div>
              {!recurringValid && (
                <p className="text-[11px] text-gray-400">
                  Pick at least one weekday and an end date.
                </p>
              )}
            </div>
          )}

          <div className="flex gap-2">
            <button
              onClick={handleAdd}
              disabled={!canAdd}
              className="flex-1 bg-cyan-500 hover:bg-cyan-600 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-semibold py-2 px-4 rounded-lg transition-colors"
            >
              Add
            </button>
            <button
              onClick={resetAddForm}
              className="px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-lg transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Active items */}
      <div className="space-y-3">
        {activeItems.length === 0 && completedItems.length === 0 ? (
          <div className="text-center py-8 text-gray-400">
            <ListChecks className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>Nothing to remember yet. Click the + button to add something!</p>
          </div>
        ) : (
          <>
            {activeItems.length > 0 && (
              <>
                <div className="text-xs font-bold text-cyan-400 uppercase tracking-wider px-2">
                  To Remember ({activeItems.length})
                </div>
                {activeItems.map(renderItem)}
              </>
            )}

            {completedItems.length > 0 && (
              <>
                <div className="border-t-2 border-gray-700 my-4 pt-4">
                  <button
                    onClick={() => setShowDone((s) => !s)}
                    className="w-full flex items-center gap-1.5 text-xs font-bold text-green-400 uppercase tracking-wider px-2 mb-3 hover:text-green-300 transition-colors"
                    title={showDone ? "Collapse done" : "Expand done"}
                  >
                    {showDone ? (
                      <ChevronDown className="w-4 h-4 flex-shrink-0" />
                    ) : (
                      <ChevronRight className="w-4 h-4 flex-shrink-0" />
                    )}
                    Done ({completedItems.length})
                  </button>
                </div>
                {showDone && completedItems.map(renderItem)}
              </>
            )}
          </>
        )}
      </div>

      <RecurrenceHistoryModal
        isOpen={!!historyItem}
        item={historyItem}
        onClose={() => setHistoryItem(null)}
      />
    </div>
  );
};

export default Remember;
